import React, { useMemo, useState } from 'react';
import { Home, PenLine } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';

export const ROOM_GROUPS = [
  { label: 'Living areas', rooms: ['Living Room', 'Family Room', 'Great Room', 'Den', 'Sunroom', 'Library', 'Media Room / Home Theater', 'Game Room', 'Foyer / Entry', 'Hallway', 'Staircase Landing', 'Conservatory'] },
  { label: 'Bedrooms', rooms: ['Primary / Master Bedroom', 'Guest Bedroom', "Kids' Bedroom", 'Nursery', 'Bedroom 2', 'Bedroom 3', 'Bedroom 4', 'Bedroom 5', 'In-law Suite'] },
  { label: 'Kitchen & dining', rooms: ['Kitchen', 'Breakfast Nook', 'Dining Room', "Butler's Pantry", 'Bar / Wine Room'] },
  { label: 'Bathrooms', rooms: ['Primary Bathroom', 'Guest Bathroom', 'Powder Room', "Kids' Bathroom"] },
  { label: 'Work & utility', rooms: ['Home Office', 'Study', 'Laundry Room', 'Mudroom', 'Gym / Fitness Room', 'Workshop', 'Garage'] },
  { label: 'More spaces', rooms: ['Basement', 'Attic', 'Loft', 'Pool House', 'Guest House', 'Balcony / Terrace', 'Porch / Patio', 'Boat House', 'Staff Quarters'] },
];
const CUSTOM = '__custom__';
const ALL_ROOMS = ROOM_GROUPS.flatMap(g => g.rooms);

// Required room (predefined list or custom name) + optional window name
export const RoomLabelPicker = ({ room, window: windowName, onRoomChange, onWindowChange, highlight = false, stepNumber = null }) => {
  const isPreset = ALL_ROOMS.includes(room);
  const [custom, setCustom] = useState(Boolean(room) && !isPreset);
  const selectValue = useMemo(() => (custom ? CUSTOM : isPreset ? room : ''), [custom, isPreset, room]);

  const onPick = (v) => {
    if (v === CUSTOM) { setCustom(true); onRoomChange(''); return; }
    setCustom(false);
    onRoomChange(v);
  };

  return (
    <div className={`mt-6 p-4 rounded-lg border transition-colors ${highlight ? 'border-amber-400 bg-amber-50' : 'border-neutral-200 bg-neutral-50'}`} data-testid="room-label-section">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-medium text-neutral-500 uppercase tracking-wider flex items-center gap-1.5">{stepNumber ? <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${room ? 'bg-amber-600 text-white' : 'bg-neutral-200 text-neutral-600'}`} data-testid="room-step-number">{stepNumber}</span> : <Home className="w-3.5 h-3.5" />} Room <span className="text-amber-600">· required</span></Label>
        {highlight && !room && <span className="text-xs text-amber-700" data-testid="room-required-hint">Choose a room to add this shade</span>}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Select value={selectValue} onValueChange={onPick}>
            <SelectTrigger className="h-10 bg-white" data-testid="room-select-trigger"><SelectValue placeholder="Select a room…" /></SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {ROOM_GROUPS.map(g => (
                <SelectGroup key={g.label}>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-neutral-400">{g.label}</SelectLabel>
                  {g.rooms.map(r => <SelectItem key={r} value={r} data-testid={`room-option-${r.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>{r}</SelectItem>)}
                </SelectGroup>
              ))}
              <SelectGroup>
                <SelectLabel className="text-[10px] uppercase tracking-wider text-neutral-400">Other</SelectLabel>
                <SelectItem value={CUSTOM} data-testid="room-option-custom"><span className="inline-flex items-center gap-1.5"><PenLine className="w-3.5 h-3.5" /> Custom name / room…</span></SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          {custom && (
            <Input
              autoFocus
              value={room}
              onChange={(e) => onRoomChange(e.target.value)}
              placeholder="Type a room name, e.g. Blue Room, Studio B"
              className="h-10 mt-2 bg-white"
              data-testid="room-label-input"
            />
          )}
        </div>
        <div>
          <Input
            value={windowName || ''}
            onChange={(e) => onWindowChange(e.target.value)}
            placeholder="Window name (optional) — e.g. Left window, Window 1"
            className="h-10 bg-white"
            data-testid="window-label-input"
          />
          <p className="text-[11px] text-neutral-500 mt-1.5">Name each window so several shades in one room stay easy to tell apart.</p>
        </div>
      </div>
    </div>
  );
};
